import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { GpsFix, Info, WifiHigh, WifiSlash, ArrowsClockwise, LinkSimpleBreak } from "@phosphor-icons/react";
import { fmtDateTime } from "@/lib/date";

const STATUS = {
  connected: { t: "Connected", c: "bg-green-100 text-green-700", Icon: WifiHigh },
  recent: { t: "Recent", c: "bg-amber-100 text-amber-800", Icon: WifiHigh },
  stale: { t: "Stale", c: "bg-orange-100 text-orange-700", Icon: WifiSlash },
  offline: { t: "Offline", c: "bg-gray-200 text-gray-600", Icon: WifiSlash },
};

export default function AssetTelemetryTab({ editing, entityPath = "assets" }) {
  const assetId = editing?.id;
  const [status, setStatus] = useState(null);
  const [imei, setImei] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!assetId) return;
    try { const { data } = await api.get(`/${entityPath}/${assetId}/telemetry`); setStatus(data); setImei(data.imei || ""); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [assetId, entityPath]);
  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    setBusy(true); setError("");
    try { await api.post(`/${entityPath}/${assetId}/telemetry/enable`); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const register = async () => {
    if (!imei.trim()) { setError("Enter the device IMEI."); return; }
    setBusy(true); setError("");
    try { await api.put(`/integrations/telematics/registry/register/${assetId}`, { imei: imei.trim() }); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const unbind = async () => {
    setBusy(true); setError("");
    try { await api.delete(`/integrations/telematics/registry/register/${assetId}`); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  if (!assetId) return <div className="text-sm text-gray-500 py-8 text-center" data-testid="telemetry-unsaved">Save the asset first to configure GPS tracking.</div>;

  const st = STATUS[status?.gateway_status] || STATUS.offline;
  const lp = status?.last_position;

  return (
    <div className="space-y-4" data-testid="asset-telemetry-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-equipr-black">
          <GpsFix size={20} weight="fill" className="text-accent-ink" />
          <h4 className="font-bold text-sm">GPS / Telematics</h4>
        </div>
        <button onClick={load} data-testid="telemetry-refresh" className="text-gray-400 hover:text-equipr-black"><ArrowsClockwise size={16} /></button>
      </div>

      {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="telemetry-error">{error}</div>}

      {!status?.enabled ? (
        <div className="border border-gray-200 p-6 text-center">
          <p className="text-sm text-gray-500 mb-3">Telemetry is not enabled for this asset.</p>
          <button onClick={enable} disabled={busy} data-testid="telemetry-enable-btn"
            className="h-9 px-5 bg-equipr-yellow text-equipr-black font-bold text-sm rounded-none hover:bg-equipr-yellow-dark disabled:opacity-50">
            {busy ? "Enabling…" : "Enable GPS tracking"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-sm ${st.c}`} data-testid="telemetry-status-badge">
              <st.Icon size={13} weight="bold" /> {st.t}
            </span>
            {status.last_packet_at && <span className="text-xs text-gray-500">Last packet: {fmtDateTime(status.last_packet_at)}</span>}
          </div>

          <div className="border border-gray-200">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">Device</div>
            <div className="p-4 space-y-3">
              <div className="flex items-end gap-2">
                <div className="flex-1"><label className={lbl}>Device IMEI</label>
                  <input className={inp} value={imei} onChange={(e) => setImei(e.target.value.replace(/\D/g, ""))} placeholder="15-digit IMEI" data-testid="telemetry-imei" /></div>
                <button onClick={register} disabled={busy} data-testid="telemetry-register-btn"
                  className="h-9 px-4 bg-equipr-black text-white text-sm font-bold rounded-none hover:bg-gray-800 disabled:opacity-50">
                  {status.imei ? "Update" : "Register"}
                </button>
                {status.imei && (
                  <button onClick={unbind} disabled={busy} data-testid="telemetry-unbind-btn" title="Unbind device"
                    className="h-9 px-3 border border-red-300 text-red-600 text-sm rounded-none hover:bg-red-50 inline-flex items-center gap-1">
                    <LinkSimpleBreak size={15} /> Unbind
                  </button>
                )}
              </div>
              <div className="text-[11px] text-gray-500">
                Configure the device firmware / SIM APN to dial <b>gps.equiprsoftware.com:5027</b> (Teltonika Codec 8/8E).
                Webhook token: <span className="font-mono">{status.webhook_token}</span>
              </div>
            </div>
          </div>

          <div className="border border-gray-200">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">Last known position</div>
            <div className="p-4">
              {lp && lp.latitude != null ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="telemetry-last-position">
                  <div className="flex justify-between border-b border-gray-50 py-0.5"><span className="text-gray-500">Latitude</span><span className="font-semibold">{lp.latitude}</span></div>
                  <div className="flex justify-between border-b border-gray-50 py-0.5"><span className="text-gray-500">Longitude</span><span className="font-semibold">{lp.longitude}</span></div>
                  <div className="flex justify-between border-b border-gray-50 py-0.5"><span className="text-gray-500">Speed</span><span className="font-semibold">{lp.speed_kmh ?? "—"} km/h</span></div>
                  <div className="flex justify-between border-b border-gray-50 py-0.5"><span className="text-gray-500">Ignition</span><span className="font-semibold">{lp.ignition == null ? "—" : lp.ignition ? "On" : "Off"}</span></div>
                  <div className="flex justify-between border-b border-gray-50 py-0.5"><span className="text-gray-500">Engine hours</span><span className="font-semibold">{lp.engine_hours ?? "—"}</span></div>
                  <div className="flex justify-between border-b border-gray-50 py-0.5"><span className="text-gray-500">Odometer</span><span className="font-semibold">{lp.odometer_km ?? "—"} km</span></div>
                  <div className="col-span-2 pt-1">
                    <a href={`https://www.google.com/maps?q=${lp.latitude},${lp.longitude}`} target="_blank" rel="noreferrer"
                      data-testid="telemetry-map-link" className="text-sm font-semibold text-accent-ink hover:underline">Open in Google Maps →</a>
                  </div>
                </div>
              ) : <p className="text-sm text-gray-400">No position received yet. Positions appear once the device connects and sends a GPS fix.</p>}
            </div>
          </div>
        </>
      )}

      <div className="border border-blue-200 bg-blue-50 text-blue-800 text-xs px-3 py-2 flex gap-2" data-testid="telemetry-note">
        <Info size={16} className="shrink-0 mt-0.5" />
        <span><b>How live positions arrive:</b> your GPS device sends raw packets to the EQUIPR Telematics Gateway, which looks up this IMEI and forwards the readings to this app. Register the device IMEI here, then give the IMEI to whoever operates your gateway so it can route packets to this asset. Positions appear below once the gateway starts forwarding.</span>
      </div>
    </div>
  );
}
